import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

import { RELEASE_SQUARESPACE_WEBSITES_RESELLER } from '#client/constants/experimentsFlags';
import { PRODUCT_SERVICE_NAME } from '#client/pages/Home/constants';

export const GET_WEBSITES_ELIGIBILITY_QUERY = gql`
  query GetWebsitesEligibility($businessEntityUuid: UUID!) {
    businessEntity(businessEntityUuid: $businessEntityUuid) {
      websites {
        websites {
          dudaActive
        }
      }
    }
  }
`;

/**
 * @typedef {object} WebsiteEligibility
 * @property {boolean} loading - Website query in flight; `is*Eligible` reflects entitlement/flag only until it resolves.
 * @property {boolean} hasDuda - Owns the Duda (legacy) website entitlement.
 * @property {boolean} hasSqsp - Owns the Squarespace reseller website entitlement.
 * @property {boolean} isSqspEligible - Show the SQSP reseller UI. Priority: owned Duda website → no; else hasSqsp → yes; else hasDuda → no; else reseller flag.
 * @property {boolean} isDudaEligible - Show the legacy Duda experience; the inverse of isSqspEligible (Duda is the default, no "neither" outcome).
 */

/**
 * Decides which website experience a BE sees. Duda ownership is read from the live website record.
 * Modeled on websites-ui `useWebsiteEligibility`; dashboard-ui reads websites via `businessEntity.websites`
 * because the root `websites(businessEntityUuid:)` query is not available here.
 *
 * @returns {WebsiteEligibility}
 */
export function useWebsiteEligibility() {
  const { businessEntityUuid, entitlements } = useBusinessEntity();
  const releaseSqspWebsitesReseller = useExperimentFlag(RELEASE_SQUARESPACE_WEBSITES_RESELLER, false);

  const { data, loading, error } = useQuery(GET_WEBSITES_ELIGIBILITY_QUERY, {
    noThrow: true,
    skip: !businessEntityUuid,
    variables: { businessEntityUuid },
  });
  useError(error);

  const hasDuda = entitlements?.has(PRODUCT_SERVICE_NAME.WEBSITE) ?? false;
  const hasSqsp = entitlements?.has(PRODUCT_SERVICE_NAME.WEBSITE_SQSP_CORE) ?? false;

  const hasActiveDudaWebsite = (data?.businessEntity?.websites?.websites ?? []).some(
    (website) => website?.dudaActive === true,
  );

  // Priority order, first match wins: Duda ownership, then entitlement, then flag.
  // On query error, fail closed for flag-based eligibility — we cannot confirm Duda
  // ownership. SQSP entitlement holders remain eligible.
  const isSqspEligible = (() => {
    if (error) {
      return hasSqsp;
    }
    if (hasActiveDudaWebsite) {
      return false;
    }
    if (hasSqsp) {
      return true;
    }
    if (hasDuda) {
      return false;
    }
    return releaseSqspWebsitesReseller;
  })();

  const isDudaEligible = !isSqspEligible;

  return {
    loading,
    hasDuda,
    hasSqsp,
    isSqspEligible,
    isDudaEligible,
  };
}
